import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';

import { setError } from '@/features/error/errorSlice';
import {
  subscriptionService,
  SubscriptionType,
  VerifyPanPayload,
  VerifyPanResponse,
  PlanType,
  PricingInfo,
  UpgradeSubscriptionPayload,
  UpgradeSubscriptionResponse,
} from '@/services/subscription.service';

import { ApiError } from '../subscription/subscriptionSlice';

const initialSubscriptionPricing: PricingInfo = {
  starter: {
    yearly: {
      price: 0,
      finalPrice: 0,
      clientLimit: 0,
    },
  },
  pro: {
    yearly: {
      price: 0,
      finalPrice: 0,
      clientLimit: 0,
    },
  },
  max: {
    yearly: {
      price: 0,
      finalPrice: 0,
      clientLimit: 0,
    },
  },
  trial: {
    durationDays: 0,
    clientLimit: 0,
  },
};

const initialGstDetails: VerifyPanResponse = {
  status: 'error',
  message: '',
  isGstRegistered: false,
  billingDetails: {
    billingName: '',
    gstin: '',
    billingAddress: '',
    isGstVerified: false,
    billingPanId: '',
  },
  error: '',
};

export interface OnboardingState {
  currentStep: number;
  totalSteps: number;
  selectedPlan: {
    planType: SubscriptionType;
    finalPrice: number;
  } | null;
  subscriptionPricing: PricingInfo;
  subscriptionPricingLoaded: boolean;
  organizationName: string;
  organizationPhone: string;
  organizationReferral: string;
  organizationPanNumber: string;
  organizationAddress: string;
  organizationState: string;
  initialProgressPercentage: number;
  finalProgressPercentage: number;
  gstDetails: VerifyPanResponse;
  gstLoading: boolean;
  upgradeSubscriptionLoading: boolean;
  upgradeSubscriptionResponse: UpgradeSubscriptionResponse | null;
}

const initialState: OnboardingState = {
  currentStep: 1,
  totalSteps: 3,
  selectedPlan: null,
  subscriptionPricing: initialSubscriptionPricing,
  subscriptionPricingLoaded: false,
  organizationName: '',
  organizationPhone: '',
  organizationReferral: '',
  organizationPanNumber: '',
  organizationAddress: '',
  organizationState: '',
  initialProgressPercentage: 0,
  finalProgressPercentage: 100 / 3,
  gstDetails: initialGstDetails,
  gstLoading: false,
  upgradeSubscriptionLoading: false,
  upgradeSubscriptionResponse: null,
};

export const getSubscriptionPricing = createAsyncThunk(
  'subscription/pricing',
  async (_, { dispatch }) => {
    try {
      const response = await subscriptionService.getPricing();
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      dispatch(
        setError(apiError.response?.data?.error || apiError.message || 'Failed to get pricing'),
      );
      throw error;
    }
  },
);

export const verifyPan = createAsyncThunk(
  'subscription/verify-gst',
  async (payload: VerifyPanPayload, { dispatch }) => {
    try {
      const response = await subscriptionService.verifyPan(payload);
      return response;
    } catch (error) {
      const apiError = error as ApiError;
      dispatch(
        setError(apiError.response?.data?.error || apiError.message || 'Failed to verify PAN'),
      );
      throw error;
    }
  },
);

export const upgradeSubscription = createAsyncThunk(
  'subscription/upgrade',
  async (payload: UpgradeSubscriptionPayload, { dispatch }) => {
    try {
      const response = await subscriptionService.upgradeSubscription(payload);
      return response;
    } catch (error: any) {
      const apiError = error as ApiError;
      dispatch(
        setError(
          apiError?.response?.data?.message ||
            apiError?.response?.data?.errors?.[0] ||
            apiError.response?.data?.error ||
            apiError.message ||
            'Failed to upgrade subscription',
        ),
      );
      throw error;
    }
  },
);

const onboardingSlice = createSlice({
  name: 'onboarding',
  initialState,
  reducers: {
    setSelectedPlan: (
      state,
      action: PayloadAction<{
        planType: SubscriptionType;
        finalPrice: number;
      } | null>,
    ) => {
      state.selectedPlan = action.payload;
    },
    setOrganizationDetails: (
      state,
      action: PayloadAction<{
        name: string;
        phone: string;
        referralOrg: string;
        panNumber: string;
        address: string;
        state: string;
      }>,
    ) => {
      state.organizationName = action.payload.name;
      state.organizationPhone = action.payload.phone;
      state.organizationReferral = action.payload.referralOrg;
      state.organizationPanNumber = action.payload.panNumber;
      state.organizationAddress = action.payload.address;
      state.organizationState = action.payload.state;
    },
    nextStep: (state) => {
      if (state.currentStep < state.totalSteps) {
        state.currentStep += 1;
        state.initialProgressPercentage = state.finalProgressPercentage;
        state.finalProgressPercentage = (state.currentStep / state.totalSteps) * 100;
      }
    },
    previousStep: (state) => {
      if (state.currentStep > 1) {
        state.currentStep -= 1;
        state.initialProgressPercentage = state.finalProgressPercentage;
        state.finalProgressPercentage = (state.currentStep / state.totalSteps) * 100;
      }
    },
    setSubscriptionPricing: (state, action: PayloadAction<PricingInfo>) => {
      state.subscriptionPricing = action.payload;
      state.subscriptionPricingLoaded = true;
    },
    setGstDetails: (state, action: PayloadAction<VerifyPanResponse>) => {
      state.gstDetails = action.payload;
    },
    resetOnboarding: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyPan.pending, (state) => {
        state.gstLoading = true;
      })
      .addCase(verifyPan.fulfilled, (state, action) => {
        state.gstLoading = false;
        state.gstDetails = action.payload;
      })
      .addCase(verifyPan.rejected, (state) => {
        state.gstLoading = false;
      })
      .addCase(getSubscriptionPricing.pending, (state) => {
        state.subscriptionPricingLoaded = false;
      })
      .addCase(getSubscriptionPricing.fulfilled, (state, action) => {
        state.subscriptionPricing = action.payload.pricing || initialSubscriptionPricing;
        state.subscriptionPricingLoaded = true;
      })
      .addCase(getSubscriptionPricing.rejected, (state) => {
        state.subscriptionPricingLoaded = false;
      })
      .addCase(upgradeSubscription.pending, (state) => {
        state.upgradeSubscriptionLoading = true;
      })
      .addCase(upgradeSubscription.fulfilled, (state, action) => {
        state.upgradeSubscriptionLoading = false;
        state.upgradeSubscriptionResponse = action.payload;
      })
      .addCase(upgradeSubscription.rejected, (state) => {
        state.upgradeSubscriptionLoading = false;
      });
  },
});

export const getOrganizationDetails = (
  currentState: OnboardingState,
  updatedFields: Partial<ReturnType<typeof setOrganizationDetails>['payload']>,
) => ({
  name: currentState.organizationName,
  phone: currentState.organizationPhone,
  referralOrg: currentState.organizationReferral,
  panNumber: currentState.organizationPanNumber,
  address: currentState.organizationAddress,
  state: currentState.organizationState,
  ...updatedFields,
});

export const {
  setSelectedPlan,
  setOrganizationDetails,
  nextStep,
  previousStep,
  setSubscriptionPricing,
  setGstDetails,
  resetOnboarding,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
